'use client';

import {
  useState,
  useLayoutEffect,
  useContext,
  createContext,
  PropsWithChildren,
} from 'react';

interface ViewportSize {
  width: number;
  height: number;
}

const ViewportSizeContext = createContext<ViewportSize | null>(null);

export function useViewportSize() {
  const viewportSize = useContext(ViewportSizeContext);

  if (viewportSize === null) {
    throw new Error(
      'useViewportSize has to be used within <ViewportSizeProvider>',
    );
  }

  return viewportSize;
}

type ViewportWidthClassType = 'compact' | 'regular' | 'extended';

export const VIEWPORT_REGULAR_MIN_WIDTH = 600;
export const VIEWPORT_EXTENDED_MIN_WIDTH = 1440;

export function useViewportWidthClassType(): ViewportWidthClassType {
  const { width } = useViewportSize();

  return width < VIEWPORT_REGULAR_MIN_WIDTH
    ? 'compact'
    : width >= VIEWPORT_EXTENDED_MIN_WIDTH
    ? 'extended'
    : 'regular';
}

export default function ViewportSizeProvider({ children }: PropsWithChildren) {
  const windowSize = useWindowSize();

  return (
    <ViewportSizeContext.Provider value={windowSize}>
      {children}
    </ViewportSizeContext.Provider>
  );
}

function useWindowSize() {
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    function updateWindowSize() {
      const { innerWidth: width, innerHeight: height } = window;
      setWindowSize({ width, height });
    }

    updateWindowSize();

    window.addEventListener('resize', updateWindowSize);

    return () => window.removeEventListener('resize', updateWindowSize);
  }, []);

  return windowSize;
}
